import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { scanCheckout, getPublicProperty } from '../lib/api';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { CHECKOUT_INSTRUCTIONS, normalizeInstructions } from '../components/CheckoutInstructionsPicker';

export default function CheckoutPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');
  const [status, setStatus] = useState('ready');
  const [message, setMessage] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [instructions, setInstructions] = useState([]);

  useEffect(() => {
    if (!slug || !token) return;
    getPublicProperty(slug, token)
      .then((res) => {
        if (res.data?.ok) setInstructions(res.data.checkoutInstructions || []);
      })
      .catch(() => {});
  }, [slug, token]);

  const renderInstructions = () => {
    const items = normalizeInstructions(instructions)
      .map((it) => {
        const meta = CHECKOUT_INSTRUCTIONS.find((m) => m.key === it.key);
        return meta ? { ...meta, note: it.note } : null;
      })
      .filter(Boolean);
    if (!items.length) return null;
    return (
      <div className="text-left" data-testid="guest-checkout-instructions">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 text-center">Before you go, please:</h3>
        <div className="space-y-2">
          {items.map(({ key, label, Icon, note }, idx) => (
            <div key={key} className="flex items-start gap-3 p-3 rounded-2xl bg-white border border-slate-100 shadow-sm" data-testid={`guest-instruction-${key}`}>
              <span className="relative flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 text-white flex items-center justify-center">
                <Icon size={20} />
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center text-white text-[10px] font-bold">{idx + 1}</span>
              </span>
              <div className="min-w-0 pt-0.5">
                <p className="text-sm font-medium text-slate-700 leading-snug">{label}</p>
                {note ? <p className="text-xs text-slate-500 mt-0.5 leading-snug" data-testid={`guest-instruction-note-${key}`}>{note}</p> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleConfirmCheckout = async () => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid checkout link');
      return;
    }

    setConfirming(true);
    setStatus('processing');
    setMessage('Processing checkout...');

    try {
      const response = await scanCheckout(slug, token);
      if (response.data.ok) {
        setStatus('success');
        setMessage('Checkout confirmed! You may close this page.');
      } else {
        setStatus('error');
        setMessage(response.data.message || 'Checkout failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Unable to process checkout. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
    }}>
      {/* Floating Orbs Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-md w-full relative z-10" data-testid="checkout-page">
        <div className="card-3d p-10 text-center">
          {/* Branded logo (high-res) — marketing exposure for physical checkout guests */}
          <img
            src="/logo-new.png"
            alt="GuestCheckout"
            className="w-16 h-16 mx-auto mb-6 object-contain drop-shadow-[0_8px_20px_rgba(15,23,42,0.18)]"
            data-testid="checkout-brand-logo"
          />
          {/* Ready State - Show Confirmation Button */}
          {status === 'ready' && (
            <div className="space-y-6 animate-slide-in">
              <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto" style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
                border: '3px solid rgba(59, 130, 246, 0.2)'
              }}>
                <AlertCircle size={48} className="text-blue-500" />
              </div>
              
              <div>
                <h1 className="text-3xl font-bold mb-3" style={{
                  background: 'linear-gradient(135deg, rgb(59 130 246) 0%, rgb(16 185 129) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Confirm Checkout
                </h1>
                <p className="text-slate-600 text-lg">
                  Please confirm you're checking out
                </p>
              </div>

              {renderInstructions()}

              <button
                onClick={handleConfirmCheckout}
                disabled={confirming}
                className="btn-3d btn-3d-accent w-full h-16 text-lg rounded-2xl font-semibold"
                data-testid="confirm-checkout-button"
              >
                {confirming ? (
                  <div className="spinner-3d w-6 h-6 border-2 mx-auto"></div>
                ) : (
                  'Confirm Checkout'
                )}
              </button>

              <p className="text-sm text-slate-500">
                The property manager will be notified immediately
              </p>
            </div>
          )}

          {/* Processing State */}
          {status === 'processing' && (
            <div className="space-y-6 animate-slide-in">
              <div className="spinner-3d mx-auto"></div>
              <p className="text-lg text-slate-600 font-medium">{message}</p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="space-y-6 animate-slide-in">
              <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto pulse-glow" style={{
                background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
                border: '3px solid #10B981'
              }}>
                <CheckCircle size={48} className="text-green-600" data-testid="success-icon" />
              </div>
              
              <div>
                <h1 className="text-3xl font-bold text-green-600 mb-3" data-testid="success-message">
                  Checkout Confirmed!
                </h1>
                <p className="text-slate-600 text-lg">
                  Thank you. The property manager has been notified.
                </p>
              </div>

              <div className="success-state">
                <p className="text-sm text-green-700 font-medium">
                  ✓ You may now close this page
                </p>
              </div>

              {renderInstructions()}
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="space-y-6 animate-slide-in">
              <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto" style={{
                background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
                border: '3px solid #EF4444'
              }}>
                <XCircle size={48} className="text-red-600" data-testid="error-icon" />
              </div>
              
              <div>
                <h1 className="text-3xl font-bold text-red-600 mb-3" data-testid="error-message">
                  {message}
                </h1>
                <p className="text-slate-600">
                  Please contact the property manager if this persists.
                </p>
              </div>

              <button
                onClick={() => setStatus('ready')}
                className="btn-3d btn-3d-outline w-full h-14 rounded-2xl font-semibold"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Powered by GuestCheckout
        </p>
      </div>
    </div>
  );
}
